import React, { useCallback, useEffect, useState } from "react";

import _ from "lodash";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ImageList,
  ImageListItem,
} from "@material-ui/core";

import { getReturns } from "api/shipment";

import { Spacer, StatusLabel } from "components/Custom";
import { ExternalLink } from "components/Custom/Links";
import { Button, DatePickerGroup, Dropdown, InputContainer } from "components/Form";
import { FormattedTextField } from "components/FormElements";
import PageTitle from "components/PageTitle";
import DefaultTable from "components/Table";
import { updateReturn } from "api/shipment";

import { useLayoutDispatch, popupNotification } from "context/LayoutContext";

import {
  Class,
  Edit,
  FolderSpecial,
  LocalShipping,
  Person,
  QrCode2,
  Photograph,
  Plus,
  ExportIcon,
} from "icons";

import { getCarrierTrackingLink } from "pages/shipments/utils";

import { CONDITIONS } from "utils/constants";
import { initialAPIListValue } from "utils/constants";
import { convertDate, timezoneFormat } from "utils/date";
import getClearAdornment from "utils/form";

import useStyles from "pages/returns/styles";
import { Link } from "react-router-dom";
import { exportToExcel } from "utils/exportToExcel";
import { convertDataToTableView } from "utils/convert";
import { useUserState } from "context/UserContext";
import parseJwt from "utils/jwt";
import { getWithExpiry } from "utils/storage";

const headers = [
  {
    id: "customer",
    render: (r, exported) =>
      exported ? (
        r.customer
      ) : (
        <Grid container alignItems="center" spacing={1}>
          <Grid item xs={1}>
            <div
              style={{
                backgroundColor: "#00CC00",
                width: "5px",
                height: "30px",
                position: "relative",
                left: "-5px",
                borderRadius: "3px",
              }}
            />
          </Grid>
          <Grid item xs={9}>
            <span title="Customer">{r.customer}</span>
          </Grid>
          {r.details?.filter((d) => d.attachments?.length > 0).length > 0 && (
            <Grid item xs={1}>
              <span title="Attachments">
                <Photograph color="#bbbb0050" />
              </span>
            </Grid>
          )}
        </Grid>
      ),
  },
  {
    id: "product_sku",
    render: (r, exported) => {
      if (exported) {
        return r.details
          .map((d) =>
            d.product ? `${d.product.sku} - (${d.condition})` : "Not Ours",
          )
          .toString();
      }
      const itemCount = r.details.length;
      return !itemCount
        ? "-"
        : itemCount === 1
        ? r.details[0].product?.sku
        : `Multiple Items (${itemCount})`;
    },
  },
  { id: "po_number" },
  {
    id: "tracking_number",
    render: (r, exported) => {
      if (exported) {
        return r.tracking_number;
      }
      const carrier = r.tracking_number.startsWith("1Z") ? "UPS" : "FEDEX";
      return (
        <ExternalLink
          to={getCarrierTrackingLink(carrier, r.tracking_number)}
          children={r.tracking_number}
        />
      );
    },
  },
  { id: "note", render: (r) => r.note || "-" },
  {
    id: "condition",
    render: (r, exported) =>
      exported ? (
        r.condition
      ) : (
        <StatusLabel status={r.condition || "Not Ours"} />
      ),
  },
  { id: "created_at", render: (r) => timezoneFormat(r.created_at) },
];

const exportHeaders = [
  ...headers,
  {
    id: "is_published",
    label: "Is Published",
    render: (r) => (r.is_published ? "Published" : "Not Published"),
  },
];

export default function Returns() {
  const [returns, setReturns] = useState(initialAPIListValue);
  const { seller_id } = parseJwt(getWithExpiry("access"));
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);
  
  const [params, setParams] = useState({
    product_sku: "",
    customer: "",
    po_number: "",
    return_number: "",
    is_published: "",
    tracking_number: "",
    not_ours: "",
    seller_id: seller_id,
    with_details: true,
    start_date: convertDate(oneMonthAgo),
    end_date: convertDate(today),
  });
  const [orderDirection, setOrderDirection] = useState("desc");
  const [orderBy, setOrderBy] = useState("created_at");
  const [exportDisabled, setExportDisabled] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedReturn, setSelectedReturn] = useState({});
  const [detailModal, setDetailModal] = useState(false);

  useEffect(() => {
    if (returns.isFetching) {
      let filters = {
        ...params,
        limit: rowsPerPage,
        offset: rowsPerPage * page,
        paginated: true,
      };

      if (orderDirection && orderBy) {
        filters.ordering = `${orderDirection === "asc" ? "" : "-"}${orderBy}`;
      }

      getReturns({
        params: filters,
        setFunc: (res) => {
          setReturns({
            ...res,
            items: _.uniqBy(res.items, (i) => i.id),
          });
        },
        paginated: true,
      });
    }
  }, [params, page, rowsPerPage, returns.isFetching, orderDirection, orderBy]);

  const onSort = (header) => {
    const isAsc = orderBy === header && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(header);
    setReturns((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
    setReturns((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangeRowsPerPage = ({ target: { value } }) => {
    setRowsPerPage(parseInt(value, 10));
    debouncedSearchHandler();
  };

  const handleChangeFilterInput = (prop) => (event) => {
    setParams({ ...params, [prop]: event.target.value });
    debouncedSearchHandler();
  };

  const clearHandler = (input) => {
    setParams({ ...params, [input]: "" });
    debouncedSearchHandler();
  };

  const clearAdornment = (input) =>
    getClearAdornment(input, clearHandler, params);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    _.debounce(() => {
      setReturns({ ...returns, isFetching: true });
    }, 500),
    [],
  );

  const handleCloseModal = (status = false) => {
    if (status) {
      setReturns((prev) => ({ ...prev, isFetching: true }));
    }
    setDetailModal(false);
    setSelectedReturn({});
  };

  const exportData = (data) => {
    setExportDisabled(true);
    const today = new Date();
    exportToExcel(
      [],
      `Returns-${today.toLocaleDateString()}`,
      true,
      convertDataToTableView(data, exportHeaders),
    );
    setExportDisabled(false);
  };

  const handleExportData = () => {
    if (returns.items.length === returns.count) {
      exportData(returns.items);
    } else {
      getReturns({
        params: {
          ...params,
          limit: returns.count,
        },
        setFunc: ({ items }) => exportData(items),
        paginated: true,
      });
    }
  };

    const handleChangeFilterSelect = (prop) => ({ target: { value } }) => {
      setParams((prev) => ({ ...prev, [prop]: value }));
      debouncedSearchHandler();
    };

  return (
    <>
      <PageTitle
        title="Returns"
        multipleButtons={true}
        button={
          <>
            {seller_id === 1 && (
              <Button
                endIcon={<Plus color="white" />}
                component={Link}
                to="/app/create-return"
                variant="contained"
                size="medium"
                color="primary-light"
              >
                Create
              </Button>
            )}
            {returns?.items?.length !== 0 && (
              <Button
                endIcon={<ExportIcon color="white" />}
                variant="contained"
                size="medium"
                color="primary-light"
                onClick={() => handleExportData()}
              >
                Export
              </Button>
            )}
          </>
        }
      />
      <InputContainer>
        <Grid container>
          <DatePickerGroup
            startDateValue={params.start_date}
            startDateOnChange={handleChangeFilterSelect("start_date")}
            endDateValue={params.end_date}
            endDateOnChange={handleChangeFilterSelect("end_date")}
            itemGridProps={{ xs: 12, sm: 6, md: 4, lg: 2, xl: 2 }}
          />
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              fullWidth
              id="customer"
              label="Customer"
              variant="outlined"
              value={params.customer}
              onChange={handleChangeFilterInput("customer")}
              InputProps={clearAdornment("customer")}
              LeftIcon={Person}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              fullWidth
              id="sku"
              label="SKU"
              variant="outlined"
              value={params.product_sku}
              onChange={handleChangeFilterInput("product_sku")}
              InputProps={clearAdornment("product_sku")}
              LeftIcon={QrCode2}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              fullWidth
              id="po_number"
              label="PO Number"
              variant="outlined"
              value={params.po_number}
              onChange={handleChangeFilterInput("po_number")}
              InputProps={clearAdornment("po_number")}
              LeftIcon={FolderSpecial}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              fullWidth
              id="tracking_number"
              label="Tracking Number"
              variant="outlined"
              value={params.tracking_number}
              onChange={handleChangeFilterInput("tracking_number")}
              InputProps={clearAdornment("tracking_number")}
              LeftIcon={LocalShipping}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Dropdown
              id="condition"
              title="Condition"
              value={params.condition}
              onChange={handleChangeFilterInput("condition")}
              items={CONDITIONS.map((c) => ({ value: c, label: c }))}
              LeftIcon={Class}
            />
          </Grid>
        </Grid>
      </InputContainer>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <DefaultTable
          headers={headers}
          data={returns}
          filters={params}
          page={page}
          rowsPerPage={rowsPerPage}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          sortActions={{ onSort, orderBy, orderDirection }}
          rowOnClick={(r) => {
            setSelectedReturn(r);
            setDetailModal(true);
          }}
          disableExportData={true}
          handleExportData={handleExportData}
          exportDisabled={exportDisabled}
          setExportDisabled={setExportDisabled}
        />

        {detailModal ? (
          <ReturnDetails
            open={detailModal}
            onClose={handleCloseModal}
            returnData={selectedReturn}
          />
        ) : null}
      </Grid>
    </>
  );
}

const detailHeaders = [
  {
    id: "product",
    render: (r) => (r.product ? r.product.sku : "-"),
  },
  {
    id: "condition",
    render: (r) => <StatusLabel status={r.condition || "Not Ours"} />,
  },
  {
    id: "images",
    render: (r) => {
      return !r.attachments.length ? (
        "-"
      ) : (
        <BasicImageList images={r.attachments} />
      );
    },
  },
];

function ReturnDetails({ returnData, open, onClose }) {
  const layoutDispatch = useLayoutDispatch();
  const { permissions } = useUserState();
  const [isFetching, setIsFetching] = useState(false);
  const [formData, setFormData] = useState({
    customer: returnData.customer,
    po_number: returnData.po_number,
    tracking_number: returnData.tracking_number,
    note: returnData.note,
  });

  const hasUpdatePermission = permissions?.includes("shipment.change_return");
  const updateDisabled = returnData.is_published || !hasUpdatePermission;

  const handleChangeFilter = (prop) => ({ target: { value } }) => {
    setFormData((prev) => ({ ...prev, [prop]: value }));
  };


  const handleUpdate = () => {
    setIsFetching(true);
    updateReturn({
      id: returnData.id,
      body: { ...formData },
      responseSetter: (res) => {
        setIsFetching(false);
        const { id } = res;
        if (!id) {
          return popupNotification({
            dispatch: layoutDispatch,
            message: "Server Error",
          });
        }
        onClose(true);
        popupNotification({
          dispatch: layoutDispatch,
          message: "Done!",
          status: "success",
        });
      },
    });
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{`${returnData.po_number}-${returnData.customer}-${returnData.tracking_number}`}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormattedTextField
              fullWidth
              id="po_number"
              label="PO Number"
              variant="outlined"
              value={formData.po_number}
              onChange={handleChangeFilter("po_number")}
              disabled={updateDisabled}
              LeftIcon={FolderSpecial}
            />
          </Grid>
          <Grid item xs={6}>
            <FormattedTextField
              fullWidth
              id="customer"
              label="Customer"
              variant="outlined"
              value={formData.customer}
              onChange={handleChangeFilter("customer")}
              disabled={updateDisabled}
              LeftIcon={Person}
            />
          </Grid>
          <Grid item xs={6}>
            <FormattedTextField
              fullWidth
              id="tracking_number"
              label="Tracking Number"
              variant="outlined"
              value={formData.tracking_number}
              onChange={handleChangeFilter("tracking_number")}
              disabled={updateDisabled}
              LeftIcon={LocalShipping}
            />
          </Grid>
          <Grid item xs={6}>
            <FormattedTextField
              fullWidth
              id="note"
              label="Note"
              variant="outlined"
              value={formData.note}
              onChange={handleChangeFilter("note")}
              disabled={updateDisabled}
              LeftIcon={Edit}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              color="primary"
              onClick={handleUpdate}
              disabled={isFetching || !hasUpdatePermission}
            >
              Update
            </Button>
          </Grid>
        </Grid>
        <Spacer height="1rem" />
        <Grid container>
          <Grid item xs={12}>
            {isFetching ? (
              <CircularProgress />
            ) : (
              <DefaultTable
                headers={detailHeaders}
                data={{ items: returnData.details, isFetching: false }}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => onClose(false)}
          color="danger"
          disabled={isFetching}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BasicImageList({ images }) {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <ImageList rowHeight={160} className={classes.tableImageList} cols={1}>
        {images.map((item) => (
          <ImageListItem key={item.id} cols={1}>
            <img
              src={item.signed_url}
              alt={item.id}
              onClick={() => window.open(item.signed_url)}
            />
          </ImageListItem>
        ))}
      </ImageList>
    </div>
  );
}
